interface TypingOptions {
  source: HTMLElement | null
  output: HTMLElement | null
  typeSpeed?: number
  mode?: 'by-word' | 'by-character'
  onComplete?: () => void
  onStart?: () => void
}

interface ChainNode {
  parent: ChainNode | null
  dom: HTMLElement
  val: Array<string | ChainNode>
}

export class Typing {
  version: string
  source: HTMLElement
  output: HTMLElement
  typeSpeed: number
  isPaused: boolean
  mode: 'by-word' | 'by-character'
  onComplete?: () => void
  onStart?: () => void
  chain: ChainNode
  isDestroyed: boolean

  constructor(opts: TypingOptions) {
    this.version = '1.1'

    if (!opts.source || !(opts.source instanceof HTMLElement)) {
      throw new Error(
        'Invalid source element: source must be a valid HTMLElement.'
      )
    }
    if (!opts.output || !(opts.output instanceof HTMLElement)) {
      throw new Error(
        'Invalid output element: output must be a valid HTMLElement.'
      )
    }

    this.source = opts.source
    this.output = opts.output
    this.typeSpeed = opts.typeSpeed || 120
    this.mode = opts.mode || 'by-character'
    this.onComplete = opts.onComplete
    this.onStart = opts.onStart
    this.isPaused = false
    this.isDestroyed = false
    this.chain = {
      parent: null,
      dom: this.output,
      val: [],
    }
  }

  private toArray<T extends Node>(items: NodeListOf<T> | NamedNodeMap): T[] {
    return Array.from(items as ArrayLike<T>)
  }

  private init(): void {
    if (this.isDestroyed) {
      return
    }
    this.chain.val = this.convert(this.source, this.chain.val)
  }

  private convert(
    dom: Node,
    arr: Array<string | ChainNode>
  ): Array<string | ChainNode> {
    const children = this.toArray(dom.childNodes)

    children.forEach((node) => {
      if (node.nodeType === 3) {
        const textContent = node.nodeValue || ''
        const items =
          this.mode === 'by-word'
            ? textContent.split(/\s+/).map((word) => word + ' ')
            : textContent.split('')
        arr = arr.concat(items)
      } else if (node.nodeType === 1) {
        const elementNode = node as HTMLElement
        let val: Array<string | ChainNode> = []
        val = this.convert(elementNode, val)
        arr.push({
          dom: elementNode,
          val: val,
          parent: null,
        })
      }
    })

    return arr
  }

  private print(dom: HTMLElement, val: string, callback: () => void): void {
    setTimeout(() => {
      if (!this.isPaused && !this.isDestroyed) {
        dom.appendChild(document.createTextNode(val))
        callback()
      }
    }, this.typeSpeed)
  }

  private play(ele: ChainNode): void {
    if (!ele || this.isPaused || this.isDestroyed) {
      return
    }
    if (!ele.val.length && ele.parent) {
      this.play(ele.parent)
      return
    }
    if (!ele.val.length) {
      if (this.onComplete && !ele.parent) {
        this.onComplete()
      }
      return
    }

    const curr = ele.val.shift()
    if (typeof curr === 'string') {
      this.print(ele.dom, curr, () => {
        if (ele.val.length) {
          this.play(ele)
        } else if (ele.parent) {
          this.play(ele.parent)
        }
      })
    } else if (curr) {
      const dom = document.createElement(curr.dom.nodeName)
      const attrs = this.toArray(curr.dom.attributes)
      ;(attrs as Attr[]).forEach((attr) => {
        dom.setAttribute(attr.name, attr.value)
      })
      ele.dom.appendChild(dom)
      curr.parent = ele
      curr.dom = dom
      this.play(curr.val.length ? curr : curr.parent)
    }
  }

  public start(): void {
    if (this.isDestroyed) {
      return
    }
    if (!this.chain.val.length) {
      this.init()
    }
    this.isPaused = false
    if (this.onStart) {
      this.onStart()
    }
    this.play(this.chain)
  }

  public stop(): void {
    this.isPaused = true
  }

  public destroy(clearOutput = true): void {
    this.isPaused = true
    this.isDestroyed = true

    if (clearOutput && this.output) {
      this.output.textContent = ''
    }

    this.source = null as unknown as HTMLElement
    this.output = null as unknown as HTMLElement
    this.chain = {
      parent: null,
      dom: null as unknown as HTMLElement,
      val: [],
    }
  }
}
